import parser from 'any-date-parser';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import { DonutChart } from '@mantine/charts';
import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconFileExcel,
  IconFilter,
  IconFilterOff,
  IconSearch,
} from '@tabler/icons-react';
import { dateToDateHourString } from '@utils/date';
import { getMarketCategoryColor } from '@utils/mappers';
import { uniqueByName } from '@utils/strings';

import {
  CountSearchAnalyticsRequestParams,
  GetSearchAnalyticsRequestParams,
  GetSearchAnalyticsSortBy,
  ProductSearchEvent,
  ProductSearchEventFilters,
} from '@interfaces/searchAnalytics.interface';

import { isApiError } from '@api/index';
import { useGetMarketCategoriesQuery } from '@api/marketCategory.api';
import {
  useCountSearchAnalyticsQuery,
  useGetSearchAnalyticsQuery,
  useGetSearchAnalyticsStatsQuery,
  useLazyGetSearchAnalyticsXlsxQuery,
} from '@api/searchAnalytics.api';

import { LayoutContextType } from '@components/layout/Layout';

type TriState = '' | 'true' | 'false';

interface DraftFilters {
  startDate: string | null;
  endDate: string | null;
  searchQuery: string;
  marketCategory: string | null;
  zeroResults: TriState;
  hasFilters: TriState;
  hasSearchQuery: TriState;
}

const FILTER_LABELS: Record<keyof ProductSearchEventFilters, string> = {
  targets: 'Target',
  reactivities: 'Reattività',
  hostSpecies: 'Specie ospite',
  conjugate: 'Coniugato',
  clonalities: 'Clonalità',
  isotypes: 'Isotipi',
  applications: 'Applicazioni',
  classifications: 'Classificazioni',
  suppliers: 'Fornitori',
};

const TRI_STATE_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: 'true', label: 'Sì' },
  { value: 'false', label: 'No' },
];

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const EMPTY_DRAFT: DraftFilters = {
  startDate: null,
  endDate: null,
  searchQuery: '',
  marketCategory: null,
  zeroResults: '',
  hasFilters: '',
  hasSearchQuery: '',
};

function toIsoDate(value: string | null, endOfDay = false): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  }
  return date.toISOString();
}

function parseTriState(value: TriState): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

function draftToCountParams(
  draft: DraftFilters,
): CountSearchAnalyticsRequestParams {
  return {
    startDate: toIsoDate(draft.startDate),
    endDate: toIsoDate(draft.endDate, true),
    searchQuery: draft.searchQuery.trim() || undefined,
    marketCategory: draft.marketCategory || undefined,
    zeroResults: parseTriState(draft.zeroResults),
    hasFilters: parseTriState(draft.hasFilters),
    hasSearchQuery: parseTriState(draft.hasSearchQuery),
  };
}

function formatFilterValue(value: string | string[]): string {
  return Array.isArray(value) ? value.join(', ') : value;
}

function getActiveFilterEntries(filters: ProductSearchEventFilters) {
  return (
    Object.entries(filters) as [
      keyof ProductSearchEventFilters,
      string | string[] | undefined,
    ][]
  ).filter(([, value]) => {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
}

function FiltersSummary({ event }: { event: ProductSearchEvent }) {
  const entries = getActiveFilterEntries(event.filters);

  if (!event.hasFilters || entries.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        —
      </Text>
    );
  }

  const preview = entries
    .slice(0, 2)
    .map(
      ([key, value]) => `${FILTER_LABELS[key]}: ${formatFilterValue(value!)}`,
    )
    .join(' · ');
  const extra = entries.length > 2 ? ` (+${entries.length - 2})` : '';

  return (
    <Tooltip
      label={
        <Stack gap={4}>
          {entries.map(([key, value]) => (
            <Text key={key} size="xs">
              {FILTER_LABELS[key]}: {formatFilterValue(value!)}
            </Text>
          ))}
        </Stack>
      }
      multiline
      maw={360}
      withArrow
    >
      <Group gap={6} wrap="nowrap">
        <Badge size="sm" variant="light" leftSection={<IconFilter size={12} />}>
          {entries.length}
        </Badge>
        <Text size="sm" lineClamp={1}>
          {preview}
          {extra}
        </Text>
      </Group>
    </Tooltip>
  );
}

function FiltersDetail({ filters }: { filters: ProductSearchEventFilters }) {
  const entries = getActiveFilterEntries(filters);

  if (entries.length === 0) {
    return (
      <Text c="dimmed" size="sm" p="sm">
        Nessun filtro
      </Text>
    );
  }

  return (
    <Group gap="xs" p="sm">
      {entries.map(([key, value]) => (
        <Badge key={key} variant="outline" size="sm">
          {FILTER_LABELS[key]}: {formatFilterValue(value!)}
        </Badge>
      ))}
    </Group>
  );
}

function TodayStatsSection() {
  const { data: stats, isLoading, isError } = useGetSearchAnalyticsStatsQuery();

  const total = stats?.total ?? 0;

  const chartData = useMemo(
    () =>
      (stats?.byMarket ?? []).map((item) => ({
        name: item.marketCategory,
        value: item.count,
        color: getMarketCategoryColor(item.marketCategory),
      })),
    [stats?.byMarket],
  );

  return (
    <Paper withBorder p="md" radius="md" w="fit-content" maw="100%">
      <Text size="sm" fw={600} mb="sm">
        Ricerche giornaliere
      </Text>

      {isLoading ? (
        <Group justify="center" py="sm">
          <Loader size="sm" />
        </Group>
      ) : isError ? (
        <Text c="red" size="sm">
          Impossibile caricare le statistiche
        </Text>
      ) : chartData.length === 0 ? (
        <Text c="dimmed" size="sm">
          Nessuna ricerca
        </Text>
      ) : (
        <Group align="center" gap="lg" wrap="nowrap">
          <DonutChart
            data={chartData}
            size={120}
            thickness={22}
            chartLabel={String(total)}
            tooltipDataSource="segment"
            withTooltip
            withLabels={false}
            styles={{ label: { fontSize: 28, fontWeight: 700 } }}
          />
          <Stack gap={6}>
            {chartData.map((item) => (
              <Group key={item.name} gap="md" wrap="nowrap">
                <Group gap={8} wrap="nowrap" w={140}>
                  <Box
                    w={10}
                    h={10}
                    style={{
                      borderRadius: '50%',
                      background: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <Text size="sm" lineClamp={1}>
                    {item.name}
                  </Text>
                </Group>
                <Text size="sm" fw={600} w={24} ta="right">
                  {item.value}
                </Text>
                <Text size="xs" c="dimmed" w={36} ta="right">
                  {total > 0 ? Math.round((item.value / total) * 100) : 0}%
                </Text>
              </Group>
            ))}
          </Stack>
        </Group>
      )}
    </Paper>
  );
}

export default function SearchStatistics() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [searchParams] = useSearchParams();

  // ==========================================================================
  // State
  // ==========================================================================
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(EMPTY_DRAFT);
  const [appliedFilters, setAppliedFilters] =
    useState<CountSearchAnalyticsRequestParams>({});

  const [listParams, setListParams] = useState<
    Pick<
      GetSearchAnalyticsRequestParams,
      'page' | 'pageLength' | 'sortBy' | 'sortOrder'
    >
  >({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 50),
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<ProductSearchEvent>
  >({
    columnAccessor: 'createdAt',
    direction: 'desc',
  });

  useEffect(() => {
    if (
      listParams.sortBy === sortStatus.columnAccessor &&
      listParams.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setListParams((prev) => ({
      ...prev,
      sortBy: sortStatus.columnAccessor as GetSearchAnalyticsSortBy,
      sortOrder: sortStatus.direction,
      page: 1,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: marketCategories = [], isLoading: isLoadingCategories } =
    useGetMarketCategoriesQuery();

  const {
    data: searchCount = { count: 0 },
    isLoading: isLoadingCount,
    isError: isErrorCount,
  } = useCountSearchAnalyticsQuery(appliedFilters);

  const {
    data = [],
    isLoading,
    isError: isErrorList,
  } = useGetSearchAnalyticsQuery({
    ...appliedFilters,
    ...listParams,
  });

  const [getSearchAnalyticsXlsx, { isLoading: isExporting }] =
    useLazyGetSearchAnalyticsXlsxQuery();

  const marketCategoryOptions = useMemo(
    () =>
      uniqueByName(marketCategories).map((category) => ({
        label: category.name,
        value: category.name,
      })),
    [marketCategories],
  );

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleApplyFilters = () => {
    setAppliedFilters(draftToCountParams(draftFilters));
    setListParams((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setDraftFilters(EMPTY_DRAFT);
    setAppliedFilters({});
    setListParams((prev) => ({
      ...prev,
      page: 1,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }));
    setSortStatus({
      columnAccessor: 'createdAt',
      direction: 'desc',
    });
  };

  const handleExportXlsx = async () => {
    try {
      const blob = await getSearchAnalyticsXlsx({
        ...appliedFilters,
        sortBy: listParams.sortBy,
        sortOrder: listParams.sortOrder,
      }).unwrap();

      const alink = document.createElement('a');
      alink.href = window.URL.createObjectURL(blob);
      alink.download = 'PG Search Analytics.xlsx';
      alink.click();
      alink.remove();
    } catch (e) {
      if (isApiError(e)) {
        showNotification({
          title: 'Errore',
          message: e.data.message,
          color: 'red',
        });
      } else {
        showNotification({
          title: 'Errore inatteso',
          message: 'Si è verificato un errore.',
          color: 'red',
        });
      }
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Statistiche ricerche',
    });
  }, [setLayoutProps]);

  const isError = isErrorCount || isErrorList;

  return (
    <Stack gap="md">
      <TodayStatsSection />

      <Stack gap="sm">
        <Group
          align="flex-end"
          gap="sm"
          grow
          preventGrowOverflow={false}
          wrap="wrap"
        >
          <DateInput
            label="Da"
            placeholder="Data inizio"
            valueFormat="DD/MM/YYYY"
            locale="it"
            maxDate={draftFilters.endDate || new Date()}
            dateParser={(value) => parser.fromString(value, 'it')}
            value={draftFilters.startDate}
            onChange={(value) =>
              setDraftFilters((prev) => ({ ...prev, startDate: value }))
            }
            clearable
            style={{ flex: '1 1 10rem', minWidth: '10rem' }}
          />
          <DateInput
            label="A"
            placeholder="Data fine"
            valueFormat="DD/MM/YYYY"
            locale="it"
            minDate={draftFilters.startDate || undefined}
            maxDate={new Date()}
            dateParser={(value) => parser.fromString(value, 'it')}
            value={draftFilters.endDate}
            onChange={(value) =>
              setDraftFilters((prev) => ({ ...prev, endDate: value }))
            }
            clearable
            style={{ flex: '1 1 10rem', minWidth: '10rem' }}
          />
          <TextInput
            label="Termine cercato"
            placeholder="Contiene..."
            leftSection={<IconSearch size={16} />}
            value={draftFilters.searchQuery}
            onChange={(e) => {
              const searchQuery = e.currentTarget.value;
              setDraftFilters((prev) => ({ ...prev, searchQuery }));
            }}
            style={{ flex: '1 1 14rem', minWidth: '12rem' }}
          />
          <Select
            label="Mercato"
            placeholder="Tutti"
            data={marketCategoryOptions}
            value={draftFilters.marketCategory}
            onChange={(value) =>
              setDraftFilters((prev) => ({ ...prev, marketCategory: value }))
            }
            disabled={isLoadingCategories}
            clearable
            searchable
            style={{ flex: '1 1 12rem', minWidth: '12rem' }}
          />
        </Group>

        <Group
          align="flex-end"
          gap="sm"
          grow
          preventGrowOverflow={false}
          wrap="wrap"
        >
          <Select
            label="Zero risultati"
            data={TRI_STATE_OPTIONS}
            value={draftFilters.zeroResults}
            onChange={(value) =>
              setDraftFilters((prev) => ({
                ...prev,
                zeroResults: (value as TriState) || '',
              }))
            }
            allowDeselect={false}
            style={{ flex: '1 1 10rem', minWidth: '10rem' }}
          />
          <Select
            label="Con filtri"
            data={TRI_STATE_OPTIONS}
            value={draftFilters.hasFilters}
            onChange={(value) =>
              setDraftFilters((prev) => ({
                ...prev,
                hasFilters: (value as TriState) || '',
              }))
            }
            allowDeselect={false}
            style={{ flex: '1 1 10rem', minWidth: '10rem' }}
          />
          <Select
            label="Con termine cercato"
            data={TRI_STATE_OPTIONS}
            value={draftFilters.hasSearchQuery}
            onChange={(value) =>
              setDraftFilters((prev) => ({
                ...prev,
                hasSearchQuery: (value as TriState) || '',
              }))
            }
            allowDeselect={false}
            style={{ flex: '1 1 10rem', minWidth: '10rem' }}
          />
          <Group
            gap="xs"
            wrap="nowrap"
            style={{ flex: '0 0 auto', minWidth: 'fit-content' }}
          >
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={handleApplyFilters}
            >
              Applica
            </Button>
            <Button
              variant="default"
              leftSection={<IconFilterOff size={16} />}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
            <Divider orientation="vertical" mx={4} />
            <Button
              variant="light"
              leftSection={<IconFileExcel size={16} />}
              onClick={handleExportXlsx}
              loading={isExporting}
            >
              Scarica excel
            </Button>
          </Group>
        </Group>
      </Stack>

      {isError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Errore"
          color="red"
          variant="light"
        >
          Impossibile caricare lo storico ricerche. Riprova più tardi.
        </Alert>
      )}

      <DataTable
        fetching={isLoading || isLoadingCount}
        idAccessor="id"
        columns={[
          {
            accessor: 'createdAt',
            title: 'Data/ora',
            sortable: true,
            render: (record) =>
              dateToDateHourString(new Date(record.createdAt)),
          },
          {
            accessor: 'user.email',
            title: 'Utente',
            render: (record) => record.user?.email || '—',
          },
          {
            accessor: 'marketCategory',
            title: 'Mercato',
            sortable: true,
          },
          {
            accessor: 'searchQuery',
            title: 'Termine cercato',
            sortable: true,
            render: (record) => record.searchQuery || '—',
          },
          {
            accessor: 'filters',
            title: 'Filtri usati',
            render: (record) => <FiltersSummary event={record} />,
          },
          {
            accessor: 'resultCount',
            title: 'N° risultati',
            sortable: true,
            textAlign: 'right',
            render: (record) =>
              record.resultCount === 0 ? (
                <Text fw={600} c="red">
                  0
                </Text>
              ) : (
                record.resultCount
              ),
          },
        ]}
        records={data}
        page={listParams.page}
        onPageChange={(page) => setListParams((prev) => ({ ...prev, page }))}
        recordsPerPage={listParams.pageLength}
        recordsPerPageOptions={PAGE_SIZE_OPTIONS}
        recordsPerPageLabel="Elementi per pagina"
        onRecordsPerPageChange={(pageLength) =>
          setListParams((prev) => ({ ...prev, pageLength, page: 1 }))
        }
        totalRecords={searchCount.count}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        rowExpansion={{
          content: ({ record }) => <FiltersDetail filters={record.filters} />,
        }}
        noRecordsText="Nessuna ricerca trovata con i filtri selezionati"
        minHeight={data.length === 0 ? 160 : undefined}
        striped
        highlightOnHover
        withTableBorder
      />
    </Stack>
  );
}
